import { MprolabContractName, MprolabContract } from '@mprolab/sdk'
import { createSlice } from '@reduxjs/toolkit'
import { cloneDeep } from 'lodash'
import { configApi } from 'state/services/config/config'

export const DEFAULT_CHUNK_PORTION = 1

export interface GlobalState {
  mprolabContracts: {
    mproContract: MprolabContract[]
    nodeOwnersContract: MprolabContract[]
    mproReferralContract: MprolabContract[]
    mproStakeRewardContract: MprolabContract[]
    uniswapRouterContract: MprolabContract[]
    usdcContract: MprolabContract[]
    classicStakeContract: MprolabContract[]
    classicDoubleStakeContract: MprolabContract[]
    mproRewardProxyContract: MprolabContract[]
    mgaRegularNodesContract: MprolabContract[]
    lightNodesContract: MprolabContract[]
    regularNodesContract: MprolabContract[]
    feeVaultContract: MprolabContract[]
    swapRouterContract: MprolabContract[]
  }
  restrictedNames: string[]
  mprolabContractsLastModifiedDate: Date | null
  lifetimeTokens: {
    [key: string]: string
  }
  restrictedNamesLastModifiedDate: Date | null
}

export const initialState: GlobalState = {
  mprolabContracts: {
    mproContract: [],
    nodeOwnersContract: [],
    mproReferralContract: [],
    mproStakeRewardContract: [],
    uniswapRouterContract: [],
    usdcContract: [],
    classicStakeContract: [],
    classicDoubleStakeContract: [],
    mproRewardProxyContract: [],
    mgaRegularNodesContract: [],
    lightNodesContract: [],
    regularNodesContract: [],
    feeVaultContract: [],
    swapRouterContract: []
  },
  mprolabContractsLastModifiedDate: null,
  lifetimeTokens: {},
  restrictedNames: [],
  restrictedNamesLastModifiedDate: null
}

export const globalSlice = createSlice({
  name: 'global',
  initialState,
  reducers: {
    setRestrictedUserNames: (state, { payload }) => {
      state.restrictedNames = payload
    },
    setRestrictedNamesLastModifiedDate: (state, { payload }) => {
      state.restrictedNamesLastModifiedDate = payload
    },
    setMprolabContractsLastModifiedDate: (state, { payload }) => {
      state.mprolabContractsLastModifiedDate = payload
    },
    setLifetimeTokens: (state, { payload }) => {
      state.lifetimeTokens[payload.account] = payload.accessToken
    },
    clearLifetimeToken: (state, { payload }) => {
      /*
       * Case-insensitively, because the key is whatever casing the connector
       * returned when the token was stored and the caller has whatever casing
       * it returns now. A delete that misses leaves the dead token in place,
       * which is the failure this action exists to end.
       */
      const wanted = String(payload.account ?? '').toLowerCase()
      for (const key of Object.keys(state.lifetimeTokens)) {
        if (key.toLowerCase() === wanted) delete state.lifetimeTokens[key]
      }
    }
  },
  extraReducers: (builder) => {
    builder.addMatcher(configApi.endpoints.getContracts.matchFulfilled, (state, { payload }) => {
      const updatedState = cloneDeep(initialState.mprolabContracts)
      payload.forEach((contract) => {
        const { name } = contract

        switch (name) {
          case MprolabContractName.MPRO:
            updatedState.mproContract.push(contract)
            break
          case MprolabContractName.NODE_OWNERS:
            updatedState.nodeOwnersContract.push(contract)
            break
          case MprolabContractName.MPRO_REFERRAL:
            updatedState.mproReferralContract.push(contract)
            break
          case MprolabContractName.MPRO_AUTO_STAKE:
          case MprolabContractName.MPRO_DUAL_AUTO_STAKE:
            updatedState.mproStakeRewardContract.push(contract)
            break
          case MprolabContractName.UNISWAP_ROUTER:
            updatedState.uniswapRouterContract.push(contract)
            break
          case MprolabContractName.USDC:
            updatedState.usdcContract.push(contract)
            break
          case MprolabContractName.MPRO_STAKE:
            updatedState.classicStakeContract.push(contract)
            break
          case MprolabContractName.MPRO_DOUBLE_STAKE:
            updatedState.classicDoubleStakeContract.push(contract)
            break
          case MprolabContractName.MPRO_REWARD_PROXY:
            updatedState.mproRewardProxyContract.push(contract)
            break
          case MprolabContractName.MGA_REGULAR_NODES:
            updatedState.mgaRegularNodesContract.push(contract)
            break
          case MprolabContractName.LIGHT_NODES:
            updatedState.lightNodesContract.push(contract)
            break
          case MprolabContractName.REGULAR_NODES:
            updatedState.regularNodesContract.push(contract)
            break
          case MprolabContractName.MPRO_FEE_VAULT:
            updatedState.feeVaultContract.push(contract)
            break
          case MprolabContractName.MPRO_SWAP_ROUTER:
            updatedState.swapRouterContract.push(contract)
            break
          default:
            break
        }
      })
      state.mprolabContracts = updatedState
    })
  }
})

export default globalSlice.reducer
